import { useEffect, useState, useRef } from 'react';
import { Linkedin, Facebook, Twitter, Github, Mail, Phone, MapPinHouse, ScanFace } from 'lucide-react';
import '../../../styles/contact-modal.css';
import { LinkPicker, LinkOption } from '../../atoms';
import api from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import { onKanbansRefresh } from '../../../utils/subscriptionEvents';

export type Contact = {
  id?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  location?: string;
  email?: string;
  phone?: string;
  twitter?: string;
  facebook?: string;
  linkedin?: string;
  github?: string;
  boardLinked?: string;
  jobLinked?: string;
};

interface Kanban {
  uid: string;
  name: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name?: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  initial?: Contact | null;
  onSubmit?: (data: Contact) => void;
  boardId?: string;
};

export default function ContactModal({ open, onClose, initial, onSubmit, boardId }: Props) {
  const [form, setForm] = useState<Contact>(initial ?? {});
  const [boards, setBoards] = useState<LinkOption[]>([]);
  const [jobs, setJobs] = useState<LinkOption[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const boardLinkedRef = useRef<string | undefined>(form.boardLinked);
  
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (initial) {
      setForm(initial);
      boardLinkedRef.current = initial.boardLinked;
    } else if (boardId) {
      setForm({ boardLinked: boardId });
      boardLinkedRef.current = boardId;
    } else {
      setForm({});
      boardLinkedRef.current = undefined;
    }
  }, [initial, boardId]);

  useEffect(() => {
    if (open) {
      fetchBoards();
    }
  }, [open]);

  useEffect(() => {
    const unsubscribe = onKanbansRefresh(() => {
      if (open) {
        fetchBoards();
      }
    });
    return unsubscribe;
  }, [open]);

  useEffect(() => {
    if (form.boardLinked) {
      fetchJobs(form.boardLinked);
    } else {
      setJobs([]);
    }
  }, [form.boardLinked]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const data = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
      const boardOptions: LinkOption[] = data.map(kanban => ({
        id: kanban.uid,
        label: kanban.name
      }));
      setBoards(boardOptions);
      
      const currentBoardLinked = boardLinkedRef.current;
      if (currentBoardLinked) {
        const boardExists = boardOptions.some(b => b.id === currentBoardLinked);
        if (!boardExists) {
          setForm(f => ({ 
            ...f, 
            boardLinked: undefined,
            jobLinked: undefined
          }));
          boardLinkedRef.current = undefined;
          setJobs([]);
        }
      }
    } catch (error) {

      setBoards([]);
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchJobs = async (kanbanUid: string) => {
    try {
      setLoadingJobs(true);
      const data = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanbanUid));
      const jobOptions: LinkOption[] = data.map(job => ({
        id: job.uid,
        label: job.company_name ? `${job.job_title} at ${job.company_name}` : job.job_title
      }));
      setJobs(jobOptions);
    } catch (error) {

      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  if (!open) return null;

  const submit = () => {
    onSubmit?.(form);
    onClose();
  };
  
  const handleBoardChange = (boardId: string | null) => {
    const newBoardId = boardId || undefined;
    boardLinkedRef.current = newBoardId;
    setForm(f => ({ 
      ...f, 
      boardLinked: newBoardId,
      jobLinked: undefined
    }));
  };

  const handleJobChange = (jobId: string | null) => {
    setForm(f => ({ ...f, jobLinked: jobId || undefined }));
  };


  return (
    <div className="contact-modal cm-overlay">
      <div className="cm-backdrop" onClick={onClose} />
      <div className="cm-surface">
        <div className="cm-header">
          <div className="cm-header-title">{initial ? 'Edit Contact' : 'Save New Contact'}</div>
          <button aria-label="Close" className="cm-icon-btn cm-header-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="cm-grid">
          <div className="cm-left">
            <div className="cm-name-row">
              <div className="cm-avatar">
                <ScanFace size={36} />
              </div>
              <div className="cm-field">
                <LabelRow label="First Name" />
                <input
                  className="cm-input"
                  placeholder="i.e: John"
                  value={form.firstName ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                />
              </div>
              <div className="cm-field">
                <LabelRow label="Last Name" />
                <input
                  className="cm-input"
                  placeholder="i.e: Smith"
                  value={form.lastName ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                />
              </div>
            </div>

            <Field
              label="Job Title"
              placeholder="i.e: CEO"
              value={form.title ?? ''}
              onChange={(v) => setForm((f) => ({ ...f, title: v }))}
            />

            <div className="cm-two-col">
              <Field
                label="Companies"
                placeholder="+ select company(s)"
                value={form.company ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, company: v }))}
              />
              <Field
                label="Location"
                placeholder="+ add location"
                value={form.location ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, location: v }))}
              />
            </div>

            <div className="cm-field">
              <label className="cm-label">Emails</label>
              <EmailBlock
                initial={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
              />
            </div>

            <div className="cm-field">
              <label className="cm-label">Phones</label>
              <PhoneBlock
                initial={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
            </div>

            <div className="cm-field cm-field--spaced">
              <label className="cm-label">Social Media</label>
            </div>
            <div className="cm-social">
              <SocialInput
                icon={<Twitter size={18} />}
                placeholder="twitter handle"
                value={form.twitter ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, twitter: v }))}
              />
              <SocialInput
                icon={<Facebook size={18} />}
                placeholder="facebook handle"
                value={form.facebook ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, facebook: v }))}
              />
              <SocialInput
                icon={<Linkedin size={18} />}
                placeholder="linkedin handle"
                value={form.linkedin ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, linkedin: v }))}
              />
              <SocialInput
                icon={<Github size={18} />}
                placeholder="github handle"
                value={form.github ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, github: v }))}
              />
            </div>
          </div>

          <div className="cm-right">
            <LinkPicker
              label="Boards"
              placeholder={loadingBoards ? "Loading boards..." : "Link board"}
              options={boards}
              value={form.boardLinked || null}
              onChange={handleBoardChange}
              disabled={loadingBoards}
            />
            <LinkPicker
              label="Jobs"
              placeholder={
                !form.boardLinked 
                  ? "Select a board first" 
                  : loadingJobs 
                    ? "Loading jobs..." 
                    : "Link job"
              }
              options={jobs}
              value={form.jobLinked || null}
              onChange={handleJobChange}
              disabled={!form.boardLinked || loadingJobs}
            />
          </div>
        </div>

        <div className="cm-footer">
          <button className="cm-btn" onClick={onClose}>
            Discard
          </button>
          <button className="cm-btn cm-btn--primary" onClick={submit}>
            {initial ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}


function LabelRow({ label }: { label: string }) {
  return (
    <label className="cm-label">
      <span>{label}</span>
      <small>Required</small>
    </label>
  );
}

function Field({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="cm-field">
      <label className="cm-label">{label}</label>
      <input
        className="cm-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SocialInput({
  icon,
  placeholder,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="cm-social-row">
      <span className="cm-social-icon">{icon}</span>
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function EmailBlock({ initial, onChange }: { initial?: string; onChange: (v: string) => void }) {
  const [email, setEmail] = useState<string>(initial ?? '');
  const [editing, setEditing] = useState<boolean>(Boolean(initial));
  const [type, setType] = useState<'work' | 'home'>('work');

  useEffect(() => {
    setEmail(initial ?? '');
    setEditing(Boolean(initial));
  }, [initial]);

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
    onChange(newEmail);
  };

  if (!editing) {
    return (
      <div className="cm-box cm-add">
        <button
          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer' }}
          onClick={() => {
            setEditing(true);
            setEmail('');
          }}
        >
          + add email
        </button>
      </div>
    );
  }

  return (
    <div className="cm-box">
      <div className="cm-box-left">
        <span className="cm-social-icon">
          <Mail size={16} />
        </span>
        <input
          className="cm-box-input"
          placeholder="example@email.com"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as 'work' | 'home')}
          className="cm-select"
        >
          <option value="work">work</option>
          <option value="home">home</option>
        </select>
        <button
          aria-label="Remove email"
          className="cm-icon-btn"
          onClick={() => {
            setEditing(false);
            setEmail('');
            onChange('');
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function PhoneBlock({ initial, onChange }: { initial?: string; onChange: (v: string) => void }) {
  const [phone, setPhone] = useState<string>(initial ?? '');
  const [editing, setEditing] = useState<boolean>(Boolean(initial));
  const [type, setType] = useState<'mobile' | 'work' | 'home'>('mobile');

  useEffect(() => {
    setPhone(initial ?? '');
    setEditing(Boolean(initial));
  }, [initial]);

  const handlePhoneChange = (newPhone: string) => {
    setPhone(newPhone);
    onChange(newPhone);
  };

  if (!editing) {
    return (
      <div className="cm-box cm-add">
        <button
          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer' }}
          onClick={() => {
            setEditing(true);
            setPhone('');
          }}
        >
          + add phone
        </button>
      </div>
    );
  }

  return (
    <div className="cm-box">
      <div className="cm-box-left">
        <span className="cm-social-icon">
          <Phone size={16} />
        </span>
        <input
          className="cm-box-input"
          placeholder="+1 212 345 6789"
          value={phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as 'mobile' | 'work' | 'home')}
          className="cm-select"
        >
          <option value="mobile">mobile</option>
          <option value="work">work</option>
          <option value="home">home</option>
        </select>
        <button
          aria-label="Remove phone"
          className="cm-icon-btn"
          onClick={() => {
            setEditing(false);
            setPhone('');
            onChange('');
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
